import { mergeTimelineEvents, toneForStatus, type MergedTimelineEvent } from './timeline';

// Shared helpers for the customs-message consoles (CUSDEC ML, Etat de Chargement, Tryptique —
// see src/components/CustomsMessageConsole.tsx). All three ADII/PortNet XML message types
// persist an identical `timeline: Array<{ at, status, detail? }>` shape, so that mapping is
// genuinely shared logic, unlike the summary fields/child tables which differ per type and
// stay as page-local slot functions.

export function buildCustomsTimelineEvents(timeline: Array<{ at: string; status: string; detail?: string }>): MergedTimelineEvent[] {
  return mergeTimelineEvents(timeline.map((entry, index) => ({
    id: `${entry.status}-${entry.at}-${index}`,
    label: formatStatusLabel(entry.status),
    detail: entry.detail,
    status: entry.status,
    at: entry.at,
    tone: toneForStatus(entry.status),
  })));
}

function formatStatusLabel(status: string): string {
  return status.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (character) => character.toUpperCase());
}

// A validation report is rendered the same way everywhere (see ValidationReportView.tsx), but
// the raw shapes differ: CUSDEC ML's is { status, errors: Issue[], warnings: Issue[],
// xsdErrors: Issue[] } (structured issue objects), while Etat de Chargement/Tryptique's is a
// plain { errors?: string[]; warnings?: string[] }. Each page normalizes its own shape into
// this common section list instead of the shell trying to understand every shape.
export interface ValidationReportSection {
  label: string;
  tone: 'error' | 'warning';
  items: string[];
}

export function formatValidationIssue(issue: { code: string; message: string }): string {
  return `${issue.code}: ${issue.message}`;
}

// Normalizer for the simple { errors?: string[]; warnings?: string[] } shape shared by Etat de
// Chargement and Tryptique.
export function simpleValidationSections(report: { errors?: string[]; warnings?: string[] } | null | undefined): ValidationReportSection[] {
  if (!report) return [];
  return [
    { label: 'Errors', tone: 'error', items: report.errors ?? [] },
    { label: 'Warnings', tone: 'warning', items: report.warnings ?? [] },
  ];
}
